/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx, css } from '@emotion/react'
import { Component, Fragment } from 'react'
import { injectIntl } from 'react-intl'
import { cloneDeep, merge, size } from 'lodash'

import type { WithEuiThemeProps } from '@elastic/eui'
import {
  EuiComboBox,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiIcon,
  EuiSpacer,
  EuiTitle,
  withEuiTheme,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type {
  DeploymentTemplateRequestBody,
  ElasticsearchClusterTopologyElement,
} from '@modules/cloud-api/v1/types'
import type { DeepPartial } from '@modules/ts-essentials'

import { getFirstEsCluster } from '@/lib/stackDeployments/selectors/stackDeployment'

import type { WrappedComponentProps } from 'react-intl'

interface Props extends WrappedComponentProps {
  instance: ElasticsearchClusterTopologyElement
  template: DeploymentTemplateRequestBody
  updateDeploymentTemplate: (deploymentTemplate: DeploymentTemplateRequestBody) => void
  addPillColor: (option: Option) => void
  pillColors: any
}

type Option = {
  label: string
  color?: string
}

type State = {
  isInvalid: boolean
}

export const pillColorDefinitions = [
  '#3185FC', //euiColorVis1
  '#00B3A4', // euiColorVis0
  '#E6C220', // euiColorVis5
  'danger',
  '#F98510', //euiColorVis7
  '#BFA180', //euiColorVis6
  '#490092', //euiColorVis3
  '#DD0A73', //euiColorAccent
  '#FEB6DB', //euiColorVis4
  '#017D73', //euiColorSecondary
  '#920000', //euiColorVis9
  'default',
  'hollow',
]

class NodeAttributeForm extends Component<Props & WithEuiThemeProps, State> {
  state = {
    isInvalid: false,
  }

  render() {
    const { instance, theme } = this.props
    const { isInvalid } = this.state

    const selectedOptions = this.getSelectedOptions()

    return (
      <Fragment>
        <EuiFlexGroup gutterSize='m'>
          <EuiFlexItem grow={2}>
            <EuiFlexGroup alignItems='baseline'>
              <EuiFlexItem>
                <EuiTitle size='xxs' css={css({ whiteSpace: 'nowrap' })}>
                  <EuiFlexGroup component='h5' gutterSize='s' alignItems='center'>
                    <EuiIcon type='logoElasticsearch' size='m' />
                    {instance.id}
                  </EuiFlexGroup>
                </EuiTitle>
              </EuiFlexItem>
              <EuiFlexItem
                grow={false}
                css={css({
                  [`@media screen and (max-width: ${theme.euiTheme.breakpoint.m}px)`]: {
                    display: 'none',
                  },
                })}
              >
                <EuiIcon type='sortRight' size='s' />
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>

          <EuiFlexItem grow={6}>
            <EuiFormRow
              label={<FormattedMessage id='node-attr.title' defaultMessage='Node attributes' />}
              helpText={
                <FormattedMessage
                  id='node-attr.help-text'
                  defaultMessage='E.g.: data:hot, data:warm'
                />
              }
              isInvalid={isInvalid}
              error={isInvalid ? 'Attribute should follow the format aaaa:bbbb' : undefined}
            >
              <EuiComboBox
                noSuggestions={true}
                selectedOptions={selectedOptions}
                onChange={this.onChange}
                onSearchChange={this.onSearchChange}
                onCreateOption={this.onCreateOption}
                isInvalid={isInvalid}
                data-test-id='node-attribute-form'
              />
            </EuiFormRow>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer />
      </Fragment>
    )
  }

  getSelectedOptions = (): Option[] => {
    const { instance, pillColors } = this.props

    const nodeAttributes = instance.elasticsearch?.node_attributes

    if (!nodeAttributes || size(nodeAttributes) < 1) {
      return []
    }

    return Object.entries(nodeAttributes).map(([key, value]) => ({
      label: `${key}:${nodeAttributes[key]}`,
      color: pillColors[`${key}:${value}`],
    }))
  }

  setPillColor = (option: Option) => {
    const { pillColors } = this.props

    if (pillColors[option.label]) {
      return pillColors[option.label]
    }

    const color = pillColorDefinitions[size(pillColors) % pillColorDefinitions.length]

    return color
  }

  changeAttributes = (selectedOptions: Option[]) => {
    const index = getFirstEsCluster({
      deployment: this.props.template.deployment_template,
    })?.plan.cluster_topology.indexOf(this.props.instance)

    if (index === undefined) {
      return
    }

    const template = cloneDeep(this.props.template)
    const deploymentTemplate = template.deployment_template

    const esResource = getFirstEsCluster({ deployment: deploymentTemplate })

    if (!esResource) {
      return
    }

    const nodeAttributes: Record<string, string> = selectedOptions.reduce(
      (result, option: Option) => {
        const [nodeAttrKey, nodeAttrValue] = option.label.split(':')

        if (nodeAttrKey && nodeAttrValue) {
          result[nodeAttrKey] = nodeAttrValue
        }

        return result
      },
      {},
    )

    const clusterTopologyElement = esResource.plan.cluster_topology[index]

    if (!clusterTopologyElement) {
      return
    }

    merge<ElasticsearchClusterTopologyElement, DeepPartial<ElasticsearchClusterTopologyElement>>(
      clusterTopologyElement,
      {
        elasticsearch: {
          node_attributes: nodeAttributes,
        },
      },
    )

    this.props.updateDeploymentTemplate(template)
  }

  onChange = (selectedOptions: Option[]) => {
    this.changeAttributes(selectedOptions)
    this.setState({
      isInvalid: false,
    })
  }

  onSearchChange = (searchValue: string) => {
    if (!searchValue) {
      this.setState({
        isInvalid: false,
      })

      return
    }

    if (isValid(searchValue)) {
      this.setState({
        isInvalid: false,
      })
    }
  }

  onCreateOption = (searchValue) => {
    const { addPillColor } = this.props
    const selectedOptions = this.getSelectedOptions()

    if (!isValid(searchValue)) {
      this.setState({ isInvalid: true })
      return false
    }

    const color = this.setPillColor({ label: searchValue })

    const newOption: Option = {
      label: searchValue,
      color,
    }

    addPillColor(newOption)

    const newSelectedOptions = selectedOptions.concat(newOption)

    this.changeAttributes(newSelectedOptions)

    return
  }
}

function isValid(value: string) {
  const regExp = /^[a-zA-Z0-9-_]+:[a-zA-Z0-9-_]+$/
  return regExp.test(value)
}

export default withEuiTheme(injectIntl(NodeAttributeForm))
